const row = (value) => value && typeof value === 'object' ? value : {};
const rows = (value) => Array.isArray(value) ? value.map(row) : [];
const id = (value) => value == null ? '' : String(value);
const number = (value) => Number.isInteger(Number(value)) && Number(value) > 0 && Number(value) <= 3660 ? Number(value) : 0;
const firstNumber = (...values) => values.map(number).find(Boolean) || 0;
const date = (value) => {
    const text = String(value !== null && value !== void 0 ? value : '').trim();
    const iso = text.match(/^(\d{4}-\d{2}-\d{2})/);
    return iso && Number.isFinite(Date.parse(`${iso[1]}T00:00:00Z`)) ? iso[1] : '';
};
const firstDate = (...values) => values.map(date).find(Boolean) || '';
const dateOffset = (start, offset) => new Date(Date.parse(`${start}T00:00:00Z`) + offset * 86400000).toISOString().slice(0, 10);
const fields = (value) => {
    const source = row(value), raw = row(source.raw);
    // Normalized mobile objects contain empty placeholders. These must not erase
    // a real date or parent ID retained in the original API/GeoJSON properties.
    return Object.assign({}, ...[raw, row(raw.properties), row(source.properties), row(source.base), source]
        .map(layer => Object.fromEntries(Object.entries(layer).filter(([, value]) => value !== '' && value !== null && value !== undefined))));
};
const itemId = (value) => { var _a, _b, _c; const data = fields(value); return id((_c = (_b = (_a = data.itemId) !== null && _a !== void 0 ? _a : data.item_travel_id) !== null && _b !== void 0 ? _b : data.item_id) !== null && _c !== void 0 ? _c : data.parent_item_id); };
const pointId = (value) => { var _a, _b; const data = fields(value); return id((_b = (_a = data.point_travel_id) !== null && _a !== void 0 ? _a : data.point_id) !== null && _b !== void 0 ? _b : data.id); };
const trackId = (value) => {
    var _a, _b, _c, _d, _e, _f;
    const data = fields(value), geo = data.travel_plan_geo;
    return id((_f = (_e = (_d = (_c = (_b = (_a = data.travel_plan_geo_id) !== null && _a !== void 0 ? _a : data.gpx_id) !== null && _b !== void 0 ? _b : data.track_id) !== null && _c !== void 0 ? _c : data.travel_plan_track_id) !== null && _d !== void 0 ? _d : row(geo).travel_plan_geo_id) !== null && _e !== void 0 ? _e : row(geo).id) !== null && _f !== void 0 ? _f : (typeof geo === 'object' ? undefined : geo));
};
const lineId = (value) => { var _a; return trackId(value) || id((_a = row(row(value).raw).id) !== null && _a !== void 0 ? _a : fields(value).id); };
const union = (...sets) => new Set(sets.flatMap(set => [...(set !== null && set !== void 0 ? set : [])]));
const merge = (map, key, values) => {
    if (!key)
        return;
    const previous = map.get(key);
    if (!previous)
        map.set(key, values);
    else if ([...values].some(value => !previous.has(value)))
        map.set(key, union(previous, values));
};
export const ALL_MAP_DAYS = 'all';
export const UNASSIGNED_MAP_DAY = 'unassigned';
export const travelMapDayStorageKey = (travelId) => `trackme.map-day.v1:${id(travelId)}`;
export const normalizeMapDaySelection = (value) => value === UNASSIGNED_MAP_DAY || /^day:[1-9]\d{0,3}$/.test(String(value)) ? String(value) : ALL_MAP_DAYS;
export function travelMapDayLabel(day) {
    if (!day.date)
        return day.label;
    const formatted = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${day.date}T00:00:00Z`));
    return `${day.label} · ${formatted}`;
}
/** Uses itinerary days, including empty/undated days; never infers days from GPX timestamps. */
export function buildTravelMapDayIndex(overviewValue, pointValues = [], lineValues = []) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    const overview = row(overviewValue), plan = row(overview.travel_plan);
    const groups = rows(overview.groups_data);
    const groupDefaults = new Map();
    const byNumber = new Map();
    const dates = new Map(), dayIds = new Map();
    const start = date(plan.date_start);
    const add = (n, dayDate = '', dayId = '', title = '') => {
        if (!number(n))
            return;
        const old = byNumber.get(n);
        const next = { value: `day:${n}`, number: n, date: dayDate || (old === null || old === void 0 ? void 0 : old.date) || '', label: `День ${n}`, title: title || (old === null || old === void 0 ? void 0 : old.title) || '' };
        if ((old === null || old === void 0 ? void 0 : old.date) && old.date !== next.date && dates.get(old.date) === next.value)
            dates.delete(old.date);
        byNumber.set(n, next);
        if (next.date)
            dates.set(next.date, next.value);
        if (dayId)
            dayIds.set(dayId, next.value);
    };
    for (const day of rows((_a = overview.map_days) !== null && _a !== void 0 ? _a : overview.travel_days)) {
        const n = number((_b = day.day_number) !== null && _b !== void 0 ? _b : day.travel_day_number), ds = date((_c = day.day_date) !== null && _c !== void 0 ? _c : day.date_start);
        const finish = date(day.date_finish);
        const span = ds && finish ? Math.max(0, Math.min(3659, Math.round((Date.parse(finish) - Date.parse(ds)) / 86400000))) : 0;
        for (let offset = 0; offset <= span; offset++)
            add(n + offset, ds ? dateOffset(ds, offset) : '', offset ? '' : id((_d = day.travel_day_id) !== null && _d !== void 0 ? _d : day.id), id(day.title));
    }
    const count = number((_e = plan.quantity_days) !== null && _e !== void 0 ? _e : plan.duration_days);
    for (let n = 1; n <= count; n++) {
        const ds = ((_f = byNumber.get(n)) === null || _f === void 0 ? void 0 : _f.date) || (start ? dateOffset(start, n - 1) : '');
        add(n, dates.has(ds) && dates.get(ds) !== `day:${n}` ? '' : ds);
    }
    for (const [position, group] of groups.entries()) {
        const ds = date(group.date_start), finish = date(group.date_finish) || ds;
        const fromDate = start && ds ? Math.round((Date.parse(ds) - Date.parse(start)) / 86400000) + 1 : 0;
        const from = firstNumber(group.travel_day_number, group.day_number, group.day_from) || number(fromDate) || number((_h = String((_g = group.day_label) !== null && _g !== void 0 ? _g : '').match(/\d+/)) === null || _h === void 0 ? void 0 : _h[0]) || position + 1;
        const span = ds && finish ? Math.max(0, Math.min(3659, Math.round((Date.parse(finish) - Date.parse(ds)) / 86400000))) : 0;
        const declaredTo = number(group.day_to), declaredFrom = number(group.day_from) || from;
        const to = from + Math.max(span, declaredTo ? declaredTo - declaredFrom : 0);
        const values = new Set();
        for (let n = from; n <= Math.min(3660, Math.max(from, to)); n++) {
            add(n, ds ? dateOffset(ds, n - from) : '', n === from ? id(group.travel_day_id) : '', id(group.day_title));
            values.add(`day:${n}`);
        }
        groupDefaults.set(group, values);
    }
    // Older snapshots may have only dated points and no itinerary metadata.
    const pointDate = (data) => firstDate(data.date, data.point_date, data.date_point);
    if (!byNumber.size)
        [...new Set(pointValues.map(point => pointDate(fields(point))).filter(Boolean))].sort().forEach((dayDate, i) => add(i + 1, dayDate));
    const index = { days: [...byNumber.values()].sort((a, b) => a.number - b.number), dates, dayIds, itemDays: new Map(), lineItemDays: new Map(), pointDays: new Map(), routePointDays: new Map(), trackDays: new Map() };
    const assignment = (source, allowGroupAnchor = true) => {
        var _a, _b;
        const params = Array.isArray(source.params)
            ? Object.fromEntries(rows(source.params).map(param => { var _a, _b; return [(_b = (_a = param.param_key) !== null && _a !== void 0 ? _a : param.items_parameters_key) !== null && _b !== void 0 ? _b : param.key, param.value]; }))
            : Object.fromEntries(Object.entries(row(source.params)).map(([key, value]) => { var _a; return [key, (_a = row(value).value) !== null && _a !== void 0 ? _a : value]; }));
        const ownFrom = firstNumber(source.plan_day_number_start, source.plan_day_number, source.planDayNumberStart, source.planDayNumber);
        const directBegin = firstDate(source.date_start, source.start_datetime, source.start_date, source.date);
        const directFinish = firstDate(source.date_finish, source.date_end, source.end_datetime, source.end_date);
        // Summary responses omit item dates but preserve their computed plan days.
        // Event parameters can contain the entire trip interval even on a boundary
        // item. travel_day_date/number belong to the shared containing group.
        const usePlanNumbers = !directBegin && !directFinish && Boolean(ownFrom);
        const begin = directBegin || (usePlanNumbers ? '' : firstDate(params.depart_date, params.start_date, params.checkin_date, params.visit_date, params.when_date, params.date_start, params.date_begin, params.date));
        const finish = directFinish || (usePlanNumbers ? '' : firstDate(params.arrive_date, params.end_date, params.checkout_date, params.finish_date, params.date_finish, params.date_end));
        const from = ownFrom || firstNumber(source.day_number, source.day_from, allowGroupAnchor ? source.travel_day_number : 0, allowGroupAnchor ? source.travelDayNumber : 0);
        const to = firstNumber(source.plan_day_number_finish, source.day_to, source.planDayNumberFinish) || from;
        let ds = begin || finish || (allowGroupAnchor && !from ? firstDate(source.travel_day_date, source.travelDayDate) : '');
        let df = finish || ds;
        if (!directBegin && !directFinish && !usePlanNumbers) {
            const role = id((_a = source.range_role_name) !== null && _a !== void 0 ? _a : source.rangeRoleName).toLowerCase();
            if (role === 'start' || number(source.range_role) === 1)
                df = ds;
            if (role === 'end' || role === 'finish' || number(source.range_role) === 3)
                ds = df;
        }
        const result = new Set();
        const useDates = Boolean(ds && index.days.some(day => day.date));
        for (const day of index.days) {
            if (useDates ? day.date && day.date >= ds && day.date <= (df < ds ? ds : df)
                : from && day.number >= from && day.number <= Math.max(from, to))
                result.add(day.value);
        }
        const explicit = dayIds.get(id((_b = source.travel_day_id) !== null && _b !== void 0 ? _b : source.travelDayId));
        if (!useDates && !from && allowGroupAnchor && explicit)
            result.add(explicit);
        return { days: result, explicit: Boolean(ds || from || (allowGroupAnchor && explicit)) };
    };
    const interval = (source) => assignment(source).days;
    const itemSpecificDays = new Map();
    for (const group of groups) {
        const groupDays = groupDefaults.get(group);
        for (const item of rows(group.items)) {
            const key = id((_j = item.item_travel_id) !== null && _j !== void 0 ? _j : item.id), own = assignment(fields(item), false);
            if (own.explicit)
                merge(itemSpecificDays, key, own.days);
            merge(index.itemDays, key, own.explicit ? own.days : groupDays !== null && groupDays !== void 0 ? groupDays : new Set());
        }
    }
    for (const item of rows(overview.items)) {
        const key = id((_k = item.item_travel_id) !== null && _k !== void 0 ? _k : item.id);
        // The itinerary summary owns item scheduling. A duplicate from the points
        // endpoint can lack plan-day metadata or carry a whole event's parameters.
        if (itemSpecificDays.has(key))
            continue;
        const own = assignment(fields(item), !index.itemDays.has(key));
        if (own.explicit)
            merge(itemSpecificDays, key, own.days);
        merge(index.itemDays, key, own.days);
    }
    // Item dates refine their containing group rather than adding its entire range.
    for (const [key, assigned] of itemSpecificDays)
        index.itemDays.set(key, assigned);
    const explicitPointDays = new Map();
    for (const point of pointValues) {
        const data = fields(point), ownDate = pointDate(data);
        const own = ownDate && dates.get(ownDate);
        const explicit = dayIds.get(id(data.travel_day_id));
        const dayNumber = firstNumber(data.plan_day_number, data.travel_day_number, data.day_number, data.planDayNumber, data.travelDayNumber);
        const numberedDay = dayNumber && ((_l = byNumber.get(dayNumber)) === null || _l === void 0 ? void 0 : _l.value);
        const assigned = ownDate ? new Set(own ? [own] : []) : explicit ? new Set([explicit]) : dayNumber ? new Set(numberedDay ? [numberedDay] : []) : (_m = index.itemDays.get(itemId(point))) !== null && _m !== void 0 ? _m : new Set();
        if (ownDate || explicit || dayNumber)
            explicitPointDays.set(pointId(point), assigned);
        index.pointDays.set(pointId(point), assigned);
    }
    const pointsById = new Map(pointValues.map(point => [pointId(point), point]));
    const parents = new Map();
    const linkedPoints = new Set();
    const root = (key) => {
        const trail = [];
        let current = key;
        while (parents.has(current) && parents.get(current) !== current) {
            trail.push(current);
            current = parents.get(current);
        }
        for (const visited of trail)
            parents.set(visited, current);
        return current;
    };
    for (const point of pointValues) {
        const data = fields(point), linkedId = id((_o = data.linked_start_id) !== null && _o !== void 0 ? _o : data.linked_point_id);
        if (linkedId && pointsById.has(linkedId)) {
            linkedPoints.add(pointId(point));
            linkedPoints.add(linkedId);
            parents.set(root(pointId(point)), root(linkedId));
        }
    }
    const itemTracks = new Map();
    for (const point of pointValues) {
        const track = trackId(point), item = itemId(point);
        if (!track || !item || linkedPoints.has(pointId(point)))
            continue;
        merge(itemTracks, item, new Set([track]));
    }
    const movementKey = (point) => {
        var _a, _b;
        const key = pointId(point), data = fields(point);
        if (linkedPoints.has(key))
            return `linked:${root(key)}`;
        const track = trackId(point), item = itemId(point);
        if (track)
            return `track:${track}`;
        if (['1', '2'].includes(id((_b = (_a = data.itemTypeId) !== null && _a !== void 0 ? _a : data.item_type_id) !== null && _b !== void 0 ? _b : data.parent_item_type_id)) && item) {
            const tracks = itemTracks.get(item);
            if ((tracks === null || tracks === void 0 ? void 0 : tracks.size) === 1)
                return `track:${[...tracks][0]}`;
            if (!(tracks === null || tracks === void 0 ? void 0 : tracks.size))
                return `item:${item}`;
        }
        return `point:${key}`;
    };
    const fallbackDays = new Map(), specificDays = new Map(), narrowDays = new Map();
    for (const point of pointValues) {
        const key = movementKey(point), assigned = index.pointDays.get(pointId(point));
        const own = explicitPointDays.get(pointId(point));
        merge(fallbackDays, key, assigned);
        if (own)
            merge(specificDays, key, own);
        else if (assigned.size === 1)
            merge(narrowDays, key, assigned);
    }
    const movementDays = new Map();
    for (const [key, fallback] of fallbackDays) {
        const assigned = specificDays.has(key) ? union(specificDays.get(key), narrowDays.get(key)) : new Set(fallback);
        // A single multi-day movement also occupies the days between its endpoints.
        if (!key.startsWith('point:') && assigned.size > 1) {
            const numbers = [...assigned].map(value => Number(value.slice(4)));
            const from = Math.min(...numbers), to = Math.max(...numbers);
            for (const day of index.days)
                if (day.number >= from && day.number <= to)
                    assigned.add(day.value);
        }
        movementDays.set(key, assigned);
    }
    for (const point of pointValues) {
        const assigned = movementDays.get(movementKey(point));
        if (!explicitPointDays.has(pointId(point))) {
            const narrowed = new Set([...index.pointDays.get(pointId(point))].filter(day => assigned.has(day)));
            index.pointDays.set(pointId(point), narrowed.size ? narrowed : assigned);
        }
        index.routePointDays.set(pointId(point), assigned);
        merge(index.lineItemDays, itemId(point), assigned);
        merge(index.trackDays, trackId(point), assigned);
    }
    for (const line of lineValues) {
        const data = fields(line), key = lineId(line), endpoints = rows(data.points);
        if (endpoints.length) {
            merge(index.trackDays, key, union(...endpoints.map(point => { var _a, _b; return (_b = (_a = index.routePointDays.get(pointId(point))) !== null && _a !== void 0 ? _a : index.pointDays.get(pointId(point))) !== null && _b !== void 0 ? _b : interval(fields(point)); })));
        }
        else if (!index.trackDays.has(key)) {
            const own = interval(data);
            merge(index.trackDays, key, own.size ? own : (_q = (_p = index.lineItemDays.get(itemId(line))) !== null && _p !== void 0 ? _p : index.itemDays.get(itemId(line))) !== null && _q !== void 0 ? _q : new Set());
        }
    }
    return index;
}
function matches(selection, assigned) {
    return selection === ALL_MAP_DAYS || (selection === UNASSIGNED_MAP_DAY ? !(assigned === null || assigned === void 0 ? void 0 : assigned.size) : Boolean(assigned === null || assigned === void 0 ? void 0 : assigned.has(selection)));
}
export function travelMapPointMatchesDay(point, selection, index) {
    return matches(selection, index.pointDays.get(pointId(point)));
}
export function travelMapLineMatchesDay(line, selection, index) {
    var _a, _b;
    return matches(selection, (_b = (_a = index.trackDays.get(lineId(line))) !== null && _a !== void 0 ? _a : index.lineItemDays.get(itemId(line))) !== null && _b !== void 0 ? _b : index.itemDays.get(itemId(line)));
}
/** Retain the complete movement geometry when only one of its endpoints belongs to the selected day. */
export function travelMapRoutePointMatchesDay(point, selection, index) {
    var _a;
    return matches(selection, union((_a = index.routePointDays.get(pointId(point))) !== null && _a !== void 0 ? _a : index.pointDays.get(pointId(point)), index.trackDays.get(trackId(point))));
}
